import * as React from 'react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';

import {
  type FileDropzoneHintVariantProps,
  type FileDropzonePromptVariantProps,
  type FileDropzoneVariantProps,
  fileDropzoneHintVariants,
  fileDropzonePromptVariants,
  fileDropzoneVariants,
} from './file-dropzone.variants';

export type FileRejectionReason = 'size' | 'type';

export interface FileRejection {
  file: File;
  reason: FileRejectionReason;
}

export interface FileDropzoneProps
  extends Omit<
      React.HTMLAttributes<HTMLDivElement>,
      'onError' | 'onDrop' | 'onChange'
    >,
    Omit<FileDropzoneVariantProps, 'dragOver'> {
  /** Hard max size per file, in bytes. Required — no library default. */
  maxSizeBytes: number;
  /**
   * Accepted MIME types or extensions. Required — no library default.
   * Matches HTML `<input accept>` semantics, accepts an array or a
   * comma-separated string.
   */
  accept: string | string[];
  /** Allow multiple files. Default: `false`. */
  multiple?: boolean;
  /**
   * Disables the dropzone. Drag, drop, and click handlers all short-
   * circuit and validation is skipped. Default: `false`.
   */
  disabled?: boolean;
  /** Called when at least one file passes validation. */
  onFilesAccepted: (files: File[]) => void;
  /**
   * Called when at least one file fails validation. The composite emits
   * structured `{ file, reason }` entries; the consumer owns the user-
   * facing message.
   */
  onFilesRejected?: (rejections: FileRejection[]) => void;
  /**
   * Render prop for per-file preview UI. Called once per file in the
   * `files` prop.
   */
  renderPreview?: (file: File) => React.ReactNode;
  /**
   * Currently-accepted files (controlled). When provided alongside
   * `renderPreview`, the composite renders previews below the prompt.
   */
  files?: File[];
}

export interface FileDropzonePromptProps
  extends React.HTMLAttributes<HTMLDivElement>,
    FileDropzonePromptVariantProps {
  /** Primary instructional text. */
  children?: React.ReactNode;
}

export interface FileDropzoneHintProps
  extends React.HTMLAttributes<HTMLDivElement>,
    FileDropzoneHintVariantProps {
  /** Secondary hint (e.g. "PNG or JPEG · up to 10MB"). Consumer-authored. */
  children?: React.ReactNode;
}

type FileDropzoneComponent = React.ForwardRefExoticComponent<
  FileDropzoneProps & React.RefAttributes<HTMLDivElement>
> & {
  Prompt: typeof FileDropzonePrompt;
  Hint: typeof FileDropzoneHint;
};

const normalizeAcceptPatterns = (accept: string | string[]): string[] => {
  const raw = Array.isArray(accept) ? accept : accept.split(',');
  return raw.map((entry) => entry.trim().toLowerCase()).filter(Boolean);
};

const fileMatchesAccept = (file: File, patterns: string[]): boolean => {
  if (patterns.length === 0) {
    return true;
  }
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();

  return patterns.some((pattern) => {
    if (pattern === '*/*') {
      return true;
    }
    if (pattern.startsWith('.')) {
      return fileName.endsWith(pattern);
    }
    if (pattern.endsWith('/*')) {
      const prefix = pattern.slice(0, pattern.length - 1);
      return fileType.startsWith(prefix);
    }
    return fileType === pattern;
  });
};

const FileDropzoneBase = React.forwardRef<HTMLDivElement, FileDropzoneProps>(
  (
    {
      className,
      maxSizeBytes,
      accept,
      multiple = false,
      disabled = false,
      onFilesAccepted,
      onFilesRejected,
      renderPreview,
      files,
      children,
      onClick,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const dragCounterRef = React.useRef(0);
    const [isDragOver, setIsDragOver] = React.useState(false);

    const acceptPatterns = React.useMemo(
      () => normalizeAcceptPatterns(accept),
      [accept],
    );
    const acceptAttr = React.useMemo(
      () => (Array.isArray(accept) ? accept.join(',') : accept),
      [accept],
    );

    const processFiles = React.useCallback(
      (incoming: File[]) => {
        if (disabled || incoming.length === 0) {
          return;
        }

        const accepted: File[] = [];
        const rejections: FileRejection[] = [];

        if (!multiple) {
          const [first, ...rest] = incoming;
          if (first) {
            if (first.size > maxSizeBytes) {
              rejections.push({ file: first, reason: 'size' });
            } else if (!fileMatchesAccept(first, acceptPatterns)) {
              rejections.push({ file: first, reason: 'type' });
            } else {
              accepted.push(first);
            }
          }
          for (const file of rest) {
            rejections.push({ file, reason: 'type' });
          }
        } else {
          for (const file of incoming) {
            if (file.size > maxSizeBytes) {
              rejections.push({ file, reason: 'size' });
              continue;
            }
            if (!fileMatchesAccept(file, acceptPatterns)) {
              rejections.push({ file, reason: 'type' });
              continue;
            }
            accepted.push(file);
          }
        }

        if (accepted.length > 0) {
          onFilesAccepted(accepted);
        }
        if (rejections.length > 0 && onFilesRejected) {
          onFilesRejected(rejections);
        }
      },
      [
        acceptPatterns,
        disabled,
        maxSizeBytes,
        multiple,
        onFilesAccepted,
        onFilesRejected,
      ],
    );

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        onClick?.(event);
        if (event.defaultPrevented || disabled) {
          return;
        }
        // The inner <input> bubbles its click up through the root; skip
        // re-opening the picker when the click originated there.
        if (event.target === inputRef.current) {
          return;
        }
        inputRef.current?.click();
      },
      [disabled, onClick],
    );

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || disabled) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          inputRef.current?.click();
        }
      },
      [disabled, onKeyDown],
    );

    const handleInputChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const list = event.target.files;
        if (!list) {
          return;
        }
        processFiles(Array.from(list));
        event.target.value = '';
      },
      [processFiles],
    );

    const handleDragEnter = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }
        event.preventDefault();
        dragCounterRef.current += 1;
        if (dragCounterRef.current === 1) {
          setIsDragOver(true);
        }
      },
      [disabled],
    );

    const handleDragLeave = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }
        event.preventDefault();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) {
          setIsDragOver(false);
        }
      },
      [disabled],
    );

    const handleDragOver = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }
        event.preventDefault();
      },
      [disabled],
    );

    const handleDrop = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }
        event.preventDefault();
        dragCounterRef.current = 0;
        setIsDragOver(false);
        const dropped = event.dataTransfer?.files;
        if (!dropped) {
          return;
        }
        processFiles(Array.from(dropped));
      },
      [disabled, processFiles],
    );

    const hasControlledFiles =
      Array.isArray(files) && files.length > 0 && Boolean(renderPreview);

    return (
      <div
        ref={ref}
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        data-hen-id={COMPONENT_IDS.FileDropzone}
        data-drag-over={isDragOver ? 'true' : 'false'}
        data-disabled={disabled ? 'true' : 'false'}
        className={cn(
          fileDropzoneVariants({ dragOver: isDragOver, disabled }),
          className,
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        {...props}
      >
        <input
          ref={inputRef}
          type='file'
          className='sr-only'
          accept={acceptAttr}
          multiple={multiple}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden='true'
          onChange={handleInputChange}
        />
        <div className='flex flex-col items-center gap-xs'>{children}</div>
        {hasControlledFiles ? (
          <div
            className='flex w-full flex-wrap items-center justify-center gap-sm pt-md'
            data-slot='previews'
          >
            {files?.map((file, index) => (
              <React.Fragment key={`${file.name}-${file.size}-${index}`}>
                {renderPreview?.(file)}
              </React.Fragment>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);
FileDropzoneBase.displayName = 'FileDropzone';

const FileDropzonePrompt = React.forwardRef<
  HTMLDivElement,
  FileDropzonePromptProps
>(({ className, variant, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(fileDropzonePromptVariants({ variant }), className)}
    data-hen-id='hen.fileDropzone.prompt'
    {...props}
  >
    {children ?? 'Drop files here, or click to upload'}
  </div>
));
FileDropzonePrompt.displayName = 'FileDropzonePrompt';

const FileDropzoneHint = React.forwardRef<
  HTMLDivElement,
  FileDropzoneHintProps
>(({ className, variant, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(fileDropzoneHintVariants({ variant }), className)}
    data-hen-id='hen.fileDropzone.hint'
    {...props}
  >
    {children}
  </div>
));
FileDropzoneHint.displayName = 'FileDropzoneHint';

const FileDropzone = FileDropzoneBase as FileDropzoneComponent;
FileDropzone.Prompt = FileDropzonePrompt;
FileDropzone.Hint = FileDropzoneHint;

export { FileDropzone, FileDropzonePrompt, FileDropzoneHint };
